import { Injectable } from "@angular/core";
import { HttpClient as AngularHttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import {
  BaseCrudService,
  CrudEndpoints,
  HttpClient,
} from "@packages/shared-business";

/**
 * Angular HTTP Client Adapter
 * Converts Angular HttpClient to framework-agnostic HttpClient interface
 */
export class AngularHttpClientAdapter implements HttpClient {
  constructor(
    private http: AngularHttpClient,
    private baseUrl: string = "",
  ) {}

  async get<R = any>(url: string, options?: any): Promise<R> {
    const fullUrl = this.baseUrl + url;
    return this.http.get<R>(fullUrl, options).toPromise() as Promise<R>;
  }

  async post<R = any>(url: string, body: any, options?: any): Promise<R> {
    const fullUrl = this.baseUrl + url;
    return this.http.post<R>(fullUrl, body, options).toPromise() as Promise<R>;
  }

  async put<R = any>(url: string, body: any, options?: any): Promise<R> {
    const fullUrl = this.baseUrl + url;
    return this.http.put<R>(fullUrl, body, options).toPromise() as Promise<R>;
  }

  async delete<R = any>(url: string, options?: any): Promise<R> {
    const fullUrl = this.baseUrl + url;
    return this.http.delete<R>(fullUrl, options).toPromise() as Promise<R>;
  }
}

/**
 * Angular CRUD Service Base
 * Wraps framework-agnostic BaseCrudService for use in Angular with Observables
 */
@Injectable({
  providedIn: "root",
})
export class AngularCrudService<T, TFormData = Partial<T>> {
  protected baseService: BaseCrudService<T, TFormData>;

  constructor(
    protected http: AngularHttpClient,
    protected endpoints: CrudEndpoints,
    protected baseUrl: string = "",
  ) {
    const httpAdapter = new AngularHttpClientAdapter(http, baseUrl);
    this.baseService = new BaseCrudService<T, TFormData>(
      httpAdapter,
      endpoints,
    );
  }

  /**
   * List all entities (returns Observable for Angular)
   */
  list(queryParams?: Record<string, any>): Observable<T[]> {
    return new Observable((observer) => {
      this.baseService
        .list(queryParams)
        .then((data) => {
          observer.next(data);
          observer.complete();
        })
        .catch((error) => observer.error(error));
    });
  }

  /**
   * Get entity by ID (returns Observable)
   */
  getById(
    id: number | string,
    queryParams?: Record<string, any>,
  ): Observable<T> {
    return new Observable((observer) => {
      this.baseService
        .getById(id, queryParams)
        .then((data) => {
          observer.next(data);
          observer.complete();
        })
        .catch((error) => observer.error(error));
    });
  }

  /**
   * Create entity (returns Observable)
   */
  create(data: TFormData): Observable<T> {
    return new Observable((observer) => {
      this.baseService
        .create(data)
        .then((result) => {
          observer.next(result);
          observer.complete();
        })
        .catch((error) => observer.error(error));
    });
  }

  /**
   * Update entity (returns Observable)
   */
  update(id: number | string, data: TFormData): Observable<T> {
    return new Observable((observer) => {
      this.baseService
        .update(id, data)
        .then((result) => {
          observer.next(result);
          observer.complete();
        })
        .catch((error) => observer.error(error));
    });
  }

  /**
   * Delete entity (returns Observable)
   */
  remove(id: number | string): Observable<void> {
    return new Observable((observer) => {
      this.baseService
        .remove(id)
        .then(() => {
          observer.next();
          observer.complete();
        })
        .catch((error) => observer.error(error));
    });
  }

  /**
   * Import from Excel (returns Observable)
   */
  importExcel(data: any): Observable<any> {
    return new Observable((observer) => {
      this.baseService
        .importExcel(data)
        .then((result) => {
          observer.next(result);
          observer.complete();
        })
        .catch((error) => observer.error(error));
    });
  }

  /**
   * Export to Excel (returns Observable)
   */
  exportExcel(filters?: Record<string, any>): Observable<any> {
    return new Observable((observer) => {
      this.baseService
        .exportExcel(filters)
        .then((result) => {
          observer.next(result);
          observer.complete();
        })
        .catch((error) => observer.error(error));
    });
  }

  /**
   * Batch create (returns Observable)
   */
  batchCreate(items: TFormData[]): Observable<T[]> {
    return new Observable((observer) => {
      this.baseService
        .batchCreate(items)
        .then((result) => {
          observer.next(result);
          observer.complete();
        })
        .catch((error) => observer.error(error));
    });
  }

  /**
   * Batch update (returns Observable)
   */
  batchUpdate(
    items: Array<{ id: number | string; data: TFormData }>,
  ): Observable<T[]> {
    return new Observable((observer) => {
      this.baseService
        .batchUpdate(items)
        .then((result) => {
          observer.next(result);
          observer.complete();
        })
        .catch((error) => observer.error(error));
    });
  }

  /**
   * Batch remove (returns Observable)
   */
  batchRemove(ids: Array<number | string>): Observable<void[]> {
    return new Observable((observer) => {
      this.baseService
        .batchRemove(ids)
        .then((result) => {
          observer.next(result);
          observer.complete();
        })
        .catch((error) => observer.error(error));
    });
  }
}
